import Checkbox from '@components/Checkbox';
import Column from '@components/Column';
import Divider from '@components/Divider';
import MiniStepper from '@components/MiniStepper';
import Radio from '@components/Radio';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import useToast from '@hooks/ui/useToast';
import { ItemOption, OptionGroup } from '@models/item';
import useOptionControl from '@store/item/hooks/useOptionControl';
import { RootState } from '@store/store';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';

import styles from './styles.module.scss';

interface Props {
    optionGroup: OptionGroup;
    itemId: string;
    noDivider?: boolean;
}

function TextOption({ optionGroup, itemId, noDivider }:Props) {
  const {
    options, name: optionGroupName, uiButtonType, minSelection,
    optionGroupId,
  } = optionGroup;
  const { orderLines } = useSelector((state: RootState) => state.item);
  const allOptions = orderLines
    .map((line) => [...line.defaultOptions, ...line.personalOptions])
    .flat()
    .map((it) => it.options)
    .flat();
  const isHotSelected = !!allOptions
    .find((it) => it.optionName === 'HOT');
  // HOT 이 선택되었을 때 얼음 옵션 그룹 미표시
  if (isHotSelected && optionGroupName === '얼음') {
    return (<Fragment/>);
  }

  return (
        <Column fullWidth>
            {(!noDivider) && (<Divider/>)}
            <Column fullWidth className={styles.container}
                    spacing={16} >
                <Row>
                    <Text size='lg' fontWeight='bold'>
                        {optionGroupName}
                    </Text>
                    {(minSelection > 0) && (
                        <span className={styles.required}>*필수</span>
                    )}
                </Row>

                {uiButtonType === 'CHECKBOX' && (
                    <CheckboxOptions options={options} optionGroupId={optionGroupId} itemId={itemId} />
                )}
                {uiButtonType === 'RADIO' && (
                    <RadioOptions options={options} optionGroupId={optionGroupId} itemId={itemId} />
                )}
            </Column>
        </Column>
  );
}

interface OptionProps {
    options: ItemOption[];
    optionGroupId: string;
    itemId: string;
}

function CheckboxOptions({ options, optionGroupId, itemId }:OptionProps) {
  const {
    selectOption, isOptionSelected, removeOption, watchOptionCount, watchOptionMaxCount,
  } = useOptionControl({ itemId });

  const toast = useToast();
  return (
        <Fragment>
            {options.map((option) => {
              const {
                name, salePrice, optionId, isChangeable, maxQuantity,
              } = option;
              return (
                    <Checkbox key={optionId}
                              className={styles.checkbox}
                              isChecked={isOptionSelected({ optionId, optionGroupId })}
                              disabled={!isChangeable}
                              onClick={() => {
                                if (isChangeable) {
                                  if (isOptionSelected({ optionId, optionGroupId })) {
                                    removeOption({ optionGroupId, option });
                                  } else {
                                    selectOption({
                                      option, optionGroupId, amount: 1,
                                    });
                                  }
                                }
                              }} >
                        <Row fullWidth justify='space-between'>
                            <Row>
                                {name}
                                {salePrice !== 0 && <Text color={colors.gray5} className={styles.price}
                                                          as='span' >
                                    {`+${salePrice.toLocaleString()}원`}
                                </Text>}
                            </Row>
                            {(maxQuantity > 1 && isChangeable && isOptionSelected({ optionId, optionGroupId })) && (
                                <MiniStepper value={watchOptionCount(optionId, optionGroupId)}
                                             min={0}
                                             increase={() => selectOption({ option, optionGroupId, amount: 1 })}
                                             decrease={() => selectOption({ option, optionGroupId, amount: -1 })}
                                             max={watchOptionMaxCount(optionId, optionGroupId)} />
                            )}
                        </Row>

                    </Checkbox>
              );
            })}
        </Fragment>
  );
}

function RadioOptions({ options, optionGroupId, itemId }:OptionProps) {
  const {
    selectOption, isOptionSelected, watchOptionCount, watchOptionMaxCount,
  } = useOptionControl({ itemId });
  return (
      <Fragment>
          {options.map((option) => {
            const {
              name, salePrice, optionId, isChangeable, maxQuantity,
            } = option;
            return (
                  <Radio value={optionId}
                         key={option.optionId}
                         disabled={!isChangeable}
                         onChange={() => selectOption({ option, optionGroupId, amount: 1 })}
                         isSelected={isOptionSelected({ optionId, optionGroupId })}>
                      <Row justify='space-between' fullWidth>
                          <Row>
                              {name}
                              {salePrice !== 0 && (
                                  <Text className={styles.price} color={colors.gray5}
                                        as='span' >
                                      {`+${salePrice.toLocaleString()}원`}
                                  </Text>
                              )}
                          </Row>
                          {(maxQuantity > 1 && isChangeable) && (
                              <MiniStepper value={watchOptionCount(optionId, optionGroupId)}
                                           min={0}
                                           increase={() => selectOption({ option, optionGroupId, amount: 1 })}
                                           decrease={() => selectOption({ option, optionGroupId, amount: -1 })}
                                           max={watchOptionMaxCount(optionId, optionGroupId)} />
                          )}
                      </Row>
                  </Radio>
            );
          })}
      </Fragment>
  );
}

export default TextOption;
